/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * AdminVenueRates — simple, focused rate entry for parent-venue child
 * workspaces (Inventory V2). Pick a venue → see all its rooms/offices/passes
 * → enter dollars → save. Also toggles publish + needs_review flags.
 */
const CENTS_TO_DOLLAR = (c) => (c == null || !Number(c)) ? "" : (Number(c) / 100).toFixed(2);

const RESOURCE_TYPE_LABEL = {
  coworking_space: "Coworking Space",
  event_venue: "Event Venue",
  meeting_room: "Meeting Room",
  private_office: "Private Office",
};

const WORKSPACE_TYPES = [
  { key: "coworking_space", label: "Coworking Space" },
  { key: "event_venue",     label: "Event Venue" },
  { key: "meeting_room",    label: "Meeting Room" },
  { key: "private_office",  label: "Private Office" },
];

export default function AdminVenueRates() {
  const [venues, setVenues] = useState([]);
  const [venueSlug, setVenueSlug] = useState(null);
  const [venueData, setVenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState({}); // resource_id → local form state
  const [saving, setSaving] = useState({}); // resource_id → boolean
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", workspace_type: "private_office", capacity: 1 });
  const [adding, setAdding] = useState(false);

  const loadVenues = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/venue-rates");
      setVenues(r.data);
      if (r.data.length > 0 && !venueSlug) setVenueSlug(r.data[0].slug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  const loadVenue = async (slug) => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/venues/${slug}/resources`);
      setVenueData(r.data);
      // Prime local state
      const local = {};
      r.data.resources.forEach(res => {
        const p = (res.workspace || {}).pricing || {};
        local[res.resource_id] = {
          hourly:   CENTS_TO_DOLLAR(p.hourly_cents),
          daily:    CENTS_TO_DOLLAR(p.daily_cents),
          half_day: CENTS_TO_DOLLAR(p.half_day_cents),
          weekly:   CENTS_TO_DOLLAR(p.weekly_cents),
          monthly:  CENTS_TO_DOLLAR(p.monthly_cents),
          day_pass: CENTS_TO_DOLLAR(p.day_pass_cents),
          booking_url:            res.external_booking_url || "",
          marketplace_published:  !!res.marketplace_published,
          needs_review:           !!res.needs_review,
        };
      });
      setRows(local);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadVenues(); }, []);
  useEffect(() => { if (venueSlug) loadVenue(venueSlug); /* eslint-disable-next-line */ }, [venueSlug]);

  const updateRow = (rid, patch) => setRows(s => ({...s, [rid]: {...s[rid], ...patch}}));

  const saveRow = async (rid) => {
    setSaving(s => ({...s, [rid]: true}));
    try {
      const row = rows[rid];
      const payload = {};
      ["hourly","daily","half_day","weekly","monthly","day_pass"].forEach(k => {
        if (row[k] !== "" && row[k] != null) payload[k] = parseFloat(row[k]);
      });
      if (row.booking_url !== undefined) payload.booking_url = row.booking_url;
      payload.marketplace_published = row.marketplace_published;
      payload.needs_review = row.needs_review;
      await api.put(`/admin/venues/resources/${rid}`, payload);
      toast.success("Saved");
      loadVenue(venueSlug);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(s => ({...s, [rid]: false})); }
  };

  const submitAddWorkspace = async (e) => {
    e.preventDefault();
    if (!venueSlug) return;
    const name = (addForm.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    setAdding(true);
    try {
      await api.post(`/admin/venues/${venueSlug}/resources`, {
        name,
        workspace_type: addForm.workspace_type,
        capacity: Math.max(1, parseInt(addForm.capacity || 1, 10)),
      });
      toast.success("Workspace added — set rates below.");
      setAddForm({ name: "", workspace_type: "private_office", capacity: 1 });
      setShowAdd(false);
      loadVenue(venueSlug);
      loadVenues();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setAdding(false); }
  };

  return (
    <div className="m-admin-section" data-testid="admin-venue-rates">
      <div className="m-card" style={{marginBottom: 16}}>
        <span className="m-eyebrow" style={{color: "var(--gold)"}}>Métier · Venue Rates</span>
        <h2 className="rec-h2" style={{margin: "6px 0 12px"}}>Enter workspace rates.</h2>
        <p className="m-muted" style={{maxWidth: 640, marginTop: -4}}>
          Simple rate entry for every parent venue&#39;s child workspaces. Save publishes to <code>/spaces/&lt;venue&gt;</code>
          immediately. Leave a field blank to show "Contact for pricing." Toggle <em>Published</em> to appear publicly.
        </p>
        <div style={{marginTop: 16}}>
          <label className="m-field" style={{maxWidth: 380}}>
            <span>Venue</span>
            <select value={venueSlug || ""} onChange={(e) => setVenueSlug(e.target.value)} data-testid="venue-rates-venue">
              {venues.map(v => (
                <option key={v.slug} value={v.slug}>
                  {v.name} · {v.state} ({v.child_published}/{v.child_total} published{v.child_needs_review ? `, ${v.child_needs_review} need review` : ""})
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="m-loading-inline">Loading…</div>
      ) : !venueData ? (
        <div className="m-empty">Pick a venue above.</div>
      ) : (
        <div className="m-card">
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom: 10, flexWrap:"wrap", gap:8}}>
            <div>
              <h3 className="m-side-title" style={{marginTop:0}}>{venueData.venue.name}</h3>
              <span className="m-muted small">{venueData.venue.address}</span>
            </div>
            <button
              className="m-btn-gold small"
              onClick={() => setShowAdd(v => !v)}
              data-testid="add-workspace-btn"
            >{showAdd ? "Close" : "+ Add workspace"}</button>
          </div>

          {showAdd && (
            <form
              onSubmit={submitAddWorkspace}
              data-testid="add-workspace-form"
              style={{
                background:"rgba(184,145,58,0.06)",
                border:"1px dashed rgba(184,145,58,0.35)",
                borderRadius:8, padding:"14px 16px", marginBottom: 16,
                display:"grid", gap:10, gridTemplateColumns:"2fr 1.4fr 1fr auto",
                alignItems:"end",
              }}
            >
              <label className="m-field small" style={{margin:0}}>
                <span>Workspace name *</span>
                <input required value={addForm.name}
                  onChange={e => setAddForm(s => ({...s, name: e.target.value}))}
                  placeholder="e.g. Coworking Day Pass, Conference Room A"
                  data-testid="add-workspace-name" />
              </label>
              <label className="m-field small" style={{margin:0}}>
                <span>Type</span>
                <select value={addForm.workspace_type}
                  onChange={e => setAddForm(s => ({...s, workspace_type: e.target.value}))}
                  data-testid="add-workspace-type">
                  {WORKSPACE_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="m-field small" style={{margin:0}}>
                <span>Capacity</span>
                <input type="number" min="1" max="500" value={addForm.capacity}
                  onChange={e => setAddForm(s => ({...s, capacity: e.target.value}))}
                  data-testid="add-workspace-capacity" />
              </label>
              <button className="m-btn-gold small" type="submit" disabled={adding} data-testid="add-workspace-submit">
                {adding ? "Adding…" : "Add"}
              </button>
              <div className="m-muted small" style={{gridColumn:"1 / -1", marginTop:-4}}>
                Added as a draft. Set rates below, then flip <em>Publish on /spaces</em> when ready.
              </div>
            </form>
          )}

          {venueData.resources.length === 0 ? (
            <div className="m-empty small">No child workspaces at this venue.</div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap: 18, marginTop: 12}}>
              {venueData.resources.map(res => {
                const row = rows[res.resource_id] || {};
                return (
                  <div key={res.resource_id} className="m-admin-row" data-testid={`venue-rate-row-${res.slug}`} style={{padding: "12px 0", borderTop: "1px solid rgba(212,168,75,0.08)", display: "flex", flexDirection: "column", gap: 10}}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:8}}>
                      <div>
                        <div className="m-admin-row-name">
                          {res.name}
                          {res.needs_review && <span className="m-badge-admin tiny" style={{marginLeft: 8, background: "#c8a24a"}}>Needs review</span>}
                          {res.marketplace_published ? (
                            <span className="m-badge-founding tiny" style={{marginLeft: 6}}>Published</span>
                          ) : (
                            <span className="m-badge-admin tiny" style={{marginLeft: 6, background: "#8a8a8a"}}>Draft</span>
                          )}
                        </div>
                        <div className="m-muted small">{RESOURCE_TYPE_LABEL[res.workspace_type] || res.workspace_type} · capacity {res.capacity}</div>
                      </div>
                    </div>
                    <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap: 10}}>
                      <label className="m-field small">
                        <span>Hourly $</span>
                        <input type="number" step="0.01" min="0" value={row.hourly ?? ""} onChange={e => updateRow(res.resource_id, {hourly: e.target.value})} data-testid={`rate-hourly-${res.slug}`} />
                      </label>
                      <label className="m-field small">
                        <span>Half-day $</span>
                        <input type="number" step="0.01" min="0" value={row.half_day ?? ""} onChange={e => updateRow(res.resource_id, {half_day: e.target.value})} />
                      </label>
                      <label className="m-field small">
                        <span>Day pass $</span>
                        <input type="number" step="0.01" min="0" value={row.day_pass ?? ""} onChange={e => updateRow(res.resource_id, {day_pass: e.target.value})} data-testid={`rate-daypass-${res.slug}`} />
                      </label>
                      <label className="m-field small">
                        <span>Daily $</span>
                        <input type="number" step="0.01" min="0" value={row.daily ?? ""} onChange={e => updateRow(res.resource_id, {daily: e.target.value})} />
                      </label>
                      <label className="m-field small">
                        <span>Weekly $</span>
                        <input type="number" step="0.01" min="0" value={row.weekly ?? ""} onChange={e => updateRow(res.resource_id, {weekly: e.target.value})} />
                      </label>
                      <label className="m-field small">
                        <span>Monthly $</span>
                        <input type="number" step="0.01" min="0" value={row.monthly ?? ""} onChange={e => updateRow(res.resource_id, {monthly: e.target.value})} />
                      </label>
                    </div>
                    <label className="m-field small" style={{maxWidth:520}}>
                      <span>Direct booking URL (optional — used for &quot;Book direct →&quot;)</span>
                      <input type="url" value={row.booking_url ?? ""} onChange={e => updateRow(res.resource_id, {booking_url: e.target.value})} placeholder="https://…" />
                    </label>
                    <div style={{display:"flex", gap: 14, alignItems: "center", flexWrap: "wrap"}}>
                      <label className="m-checkbox-inline" style={{display:"flex", gap: 6, fontSize: 13, color:"var(--m-muted)", cursor:"pointer"}}>
                        <input type="checkbox" checked={!!row.marketplace_published} onChange={e => updateRow(res.resource_id, {marketplace_published: e.target.checked})} data-testid={`publish-${res.slug}`} />
                        Publish on /spaces
                      </label>
                      <label className="m-checkbox-inline" style={{display:"flex", gap: 6, fontSize: 13, color:"var(--m-muted)", cursor:"pointer"}}>
                        <input type="checkbox" checked={!!row.needs_review} onChange={e => updateRow(res.resource_id, {needs_review: e.target.checked})} />
                        Needs review
                      </label>
                      <button className="m-btn-gold small" onClick={() => saveRow(res.resource_id)} disabled={saving[res.resource_id]} data-testid={`save-${res.slug}`}>
                        {saving[res.resource_id] ? "Saving…" : "Save rates"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
